// App store: one snapshot { status, state, index, today, error } shared by every component.
// The adapter is chosen at build time by __FRAZIER_MODE__: 'server' talks to the Node API,
// 'local' runs the same applyAction reducer in the browser and persists to localStorage.
import { useEffect, useState } from 'preact/hooks';
import * as core from '../shared/core.js';
import { buildIndex } from '../shared/derive.js';
import { todayISO } from '../shared/dates.js';
import { createSampleState } from '../shared/seed.js';
import { toast } from './components/ui.jsx';

/* global __FRAZIER_MODE__ */
// Replaced by a string literal at build time (scripts/build*.mjs), so the unused adapter is dropped from the bundle.
export const MODE = __FRAZIER_MODE__;

const { applyAction, createId, ValidationError } = core;
const STORAGE_KEY = 'frazier-local-v1';
const THEME_KEY = 'frazier-theme';
const PERSON_KEY = 'frazier-person';
const REFRESH_MS = 60_000;
const FOCUS_REFRESH_MS = 15_000;

// ---------- snapshot ----------

const indexCache = new WeakMap();

function indexFor(state) {
  if (!state) return null;
  let index = indexCache.get(state);
  if (!index) {
    index = buildIndex(state);
    indexCache.set(state, index);
  }
  return index;
}

// ---------- device preferences (theme, "who am I on this device") ----------

function readPref(key) {
  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function writePref(key, value) {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Storage blocked: the preference lasts for this page view only.
  }
}

// The Workspace filter last picked on this device ('' = All): the Workspace nav link reopens it (My Work in one click).
const WS_FILTER_KEY = 'frazier-ws-filter';
export const workspaceFilterPref = () => readPref(WS_FILTER_KEY);
export const setWorkspaceFilterPref = (filter) => writePref(WS_FILTER_KEY, filter === 'all' ? '' : filter);

const normalizeTheme = (t) => (t === 'light' ? 'light' : 'dark');

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'light' ? '#F7F7F5' : '#0B0F0D');
}

let storedPerson = readPref(PERSON_KEY);
const initialTheme = normalizeTheme(readPref(THEME_KEY));
applyTheme(initialTheme); // before the first render: no flash of the wrong theme

// The stored person only counts while they are still on the team.
function personIn(state, id) {
  if (!id || !state || !Array.isArray(state.team)) return '';
  return state.team.some((m) => m.id === id) ? id : '';
}

// authRequired: false in local mode and when the server runs with FRAZIER_AUTH=off (hide "Sign out" then).
// user: the signed-in account (server mode, DESIGN §8) — { id, name, role, department, loginId, isAdmin,
// temporaryPassword /* still on a password someone else set */ } | null. notice: why the last session ended ('' = none).
// While someone is signed in, currentPerson IS that user (the name switcher is replaced by their user card).
let snapshot = {
  status: 'loading', state: null, index: null, today: todayISO(), error: null, authRequired: MODE === 'server',
  theme: initialTheme, currentPerson: '', user: null, notice: '',
};
const listeners = new Set();

function update(patch) {
  const next = { ...snapshot, ...patch };
  if ('state' in patch || 'user' in patch) {
    if ('state' in patch) next.index = indexFor(next.state);
    next.currentPerson = personIn(next.state, next.user ? next.user.id : storedPerson);
  }
  snapshot = next;
  for (const fn of listeners) fn(next);
}

export function getTheme() {
  return snapshot.theme;
}

// setTheme('dark' | 'light') — stored in localStorage 'frazier-theme'.
export function setTheme(theme) {
  const t = normalizeTheme(theme);
  writePref(THEME_KEY, t);
  applyTheme(t);
  if (t !== snapshot.theme) update({ theme: t });
}

export function toggleTheme() {
  setTheme(snapshot.theme === 'light' ? 'dark' : 'light');
}

// Team member id of the person using this device ('' = not chosen). Sent as actorId with every action.
export function getCurrentPerson() {
  return snapshot.currentPerson;
}

// The signed-in account (server mode): the team record (fresh after edits) over the session copy. null otherwise.
export function accountOf(snap = snapshot) {
  const u = snap.user;
  if (!u) return null;
  const m = snap.index?.teamById?.get(u.id);
  return m ? { ...u, ...m } : u;
}

// Admin rights on People (DESIGN §8). Local mode and a server without sign-in manage freely; the server enforces.
export function canManageTeam(snap = snapshot) {
  if (MODE !== 'server' || snap.authRequired === false) return true;
  return !!accountOf(snap)?.isAdmin;
}

// Disabled while someone is signed in (server mode): the session decides who you are.
export function setCurrentPerson(id) {
  if (snapshot.user) return;
  storedPerson = String(id || '');
  writePref(PERSON_KEY, storedPerson);
  update({ currentPerson: personIn(snapshot.state, storedPerson) });
}

let stateSeq = 0; // bumps whenever state is replaced, so a slower background load cannot overwrite newer state

function setState(state, extra = {}) {
  stateSeq++;
  update({ status: 'ready', state, error: null, ...extra });
}

// Current snapshot for code outside components (event handlers, form openers).
export function getSnapshot() {
  return snapshot;
}

// → { status: 'loading'|'ready'|'error'|'unauthenticated', state, index, today, error, authRequired, theme, currentPerson, user }
export function useStore() {
  const [snap, setSnap] = useState(snapshot);
  useEffect(() => {
    listeners.add(setSnap);
    setSnap(snapshot);
    return () => listeners.delete(setSnap);
  }, []);
  return snap;
}

// ---------- adapters ----------

class HttpError extends Error {
  constructor(status, message) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

function statusMessage(status) {
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 413) return 'That request is too large.';
  if (status === 429) return 'Too many requests. Try again later.';
  if (status >= 500) return 'Something went wrong on the server. Try again.';
  return 'The request failed. Try again.';
}

async function api(method, url, body) {
  let res;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new HttpError(0, 'Could not reach the server. Check your connection.');
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const serverMessage = res.status !== 401 && data && typeof data.error === 'string' ? data.error : '';
    throw new HttpError(res.status, serverMessage || statusMessage(res.status));
  }
  return { data, text };
}

function httpAdapter() {
  return {
    // `checkSession`: ask /api/session first, so a signed-out visitor gets the sign-in screen
    // without a 401 on /api/state (which the browser would log as a console error).
    async load({ checkSession = false } = {}) {
      const session = checkSession ? await this.session() : undefined;
      const { data, text } = await api('GET', '/api/state');
      if (!data || !data.state) throw new HttpError(500, 'The server returned an unexpected response.');
      return { state: data.state, text, session };
    },
    // → { authRequired, user } (throws a 401 HttpError when sign-in is required and missing).
    async session() {
      const { data } = await api('GET', '/api/session');
      if (!data) return undefined;
      const authRequired = data.authRequired !== false;
      if (authRequired && !data.authenticated) throw new HttpError(401, '');
      return { authRequired, user: data.user && data.user.id ? data.user : null };
    },
    async act(type, payload, actorId) {
      const { data } = await api('POST', '/api/actions', { type, payload, today: todayISO(), actorId });
      if (!data || !data.state) throw new HttpError(500, 'The server returned an unexpected response.');
      return { state: data.state, result: data.result };
    },
  };
}

function isStateLike(s) {
  return !!s && Number.isInteger(s.version) && s.version >= 1 && ['team', 'clients', 'months', 'batches', 'events'].every((k) => Array.isArray(s[k]));
}

// v1 → v2 migration lives in shared/core.js (migrateState); tolerate a core without it.
const migrate = (s) => (typeof core.migrateState === 'function' ? core.migrateState(s) : s);

function localAdapter() {
  let state = null;
  const read = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return isStateLike(parsed) ? migrate(parsed) : null;
    } catch {
      return null;
    }
  };
  const write = (s) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // Storage blocked or full: keep working in memory.
    }
  };
  return {
    load() {
      state = read();
      if (!state) {
        state = createSampleState({ today: todayISO(), now: new Date().toISOString(), newId: createId });
        write(state);
      }
      return { state, text: '' };
    },
    act(type, payload, actorId) {
      const { state: next, result } = applyAction(state, { type, payload }, { now: new Date().toISOString(), today: todayISO(), newId: createId, actorId });
      state = next;
      write(next);
      return { state: next, result };
    },
  };
}

// ---------- loading ----------

let adapter = null;
let lastLoadAt = 0;
let lastText = '';
let loading = null;

async function load({ background = false, checkSession = !background && !snapshot.state } = {}) {
  const seqAtStart = stateSeq;
  if (!background && !snapshot.state) update({ status: 'loading', error: null });
  try {
    const { state, text, session } = await adapter.load({ checkSession });
    lastLoadAt = Date.now();
    if (stateSeq !== seqAtStart) return;
    if (session) update(session);
    if (background && text && text === lastText) return; // unchanged on the server
    lastText = text;
    setState(state);
  } catch (err) {
    if (err.status === 401) {
      signedOut({ authRequired: true });
      return;
    }
    if (snapshot.state) {
      if (!background) toast(err.message || 'Could not refresh the workspace.', { tone: 'danger' });
      return;
    }
    if (!(err instanceof HttpError)) console.error(err);
    update({ status: 'error', error: err instanceof HttpError ? err.message : 'Could not load the workspace.' });
  }
}

// Back to the sign-in screen (session gone, expired or ended). A session that ends while someone is signed in (an admin
// reset their password or switched the account off) says so on the sign-in card (`notice`).
const ENDED_NOTICE = 'Your session ended because your password changed or your account was updated. Sign in with your current password — if an admin reset it, ask them for the new one.';
function signedOut(extra = {}) {
  const notice = extra.notice ?? (snapshot.user ? ENDED_NOTICE : '');
  lastText = '';
  stateSeq++;
  update({ status: 'unauthenticated', state: null, error: null, user: null, ...extra, notice });
}

// Focus: re-read who is signed in (an admin may have changed their role, or deactivated them).
async function refreshSession() {
  if (MODE !== 'server' || !snapshot.user || !adapter?.session) return;
  try {
    const session = await adapter.session();
    if (session && snapshot.user) update(session);
  } catch (err) {
    if (err.status === 401) signedOut();
  }
}

function loadOnce(options) {
  if (!loading) loading = load(options).finally(() => { loading = null; });
  return loading;
}

function refreshToday() {
  const today = todayISO();
  if (today !== snapshot.today) update({ today });
}

function backgroundRefresh(minAge) {
  refreshToday();
  if (MODE !== 'server' || snapshot.status !== 'ready' || Date.now() - lastLoadAt < minAge) return;
  if (document.visibilityState !== 'visible') return;
  loadOnce({ background: true });
}

let started = false;

export function initStore() {
  if (started) return;
  started = true;
  adapter = __FRAZIER_MODE__ === 'server' ? httpAdapter() : localAdapter();
  loadOnce();
  const onFocus = () => {
    if (document.visibilityState === 'visible' && Date.now() - lastLoadAt >= FOCUS_REFRESH_MS) refreshSession();
    backgroundRefresh(FOCUS_REFRESH_MS);
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);
  setInterval(() => backgroundRefresh(REFRESH_MS - 1000), REFRESH_MS);
  if (MODE === 'local') {
    // Another tab of the local preview changed the data.
    window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) loadOnce({ background: true }); });
  }
}

export function reload() {
  return loadOnce();
}

// ---------- actions ----------

let queue = Promise.resolve();

// dispatch('client.create', { … }) → Promise<result>. Actions run one at a time, in order.
// On failure shows a danger toast and rejects with Error(message) — callers should catch (e.g. to keep a form open).
// `{ toast: false }` skips that toast for callers that show the message inline (e.g. the batch link editor).
export function dispatch(type, payload = {}, { toast: showToast = true } = {}) {
  const run = async () => {
    try {
      const { state, result } = await adapter.act(type, payload, snapshot.currentPerson || '');
      lastText = '';
      setState(state);
      return result;
    } catch (err) {
      if (err.status === 401) signedOut();
      const known = err instanceof ValidationError || err instanceof HttpError || err?.name === 'ValidationError';
      if (!known) console.error(err);
      const message = known ? err.message : 'Something went wrong. Try again.';
      if (showToast || err.status === 401) toast(message, { tone: 'danger' });
      throw new Error(message);
    }
  };
  if (!adapter) return Promise.reject(new Error('The store is not ready.'));
  const result = queue.then(run, run);
  queue = result.catch(() => {});
  return result;
}

// ---------- session (server mode only; no-ops in local mode) ----------

const LOGIN_KEY = 'frazier-login';

// The name or login ID typed at the last successful sign-in on this device (pre-fills the sign-in form).
export function lastLoginId() {
  return readPref(LOGIN_KEY);
}

// login(name or login ID, password) — DESIGN §8. Rejects with Error('Incorrect name or password.') and friends.
export async function login(loginId, password) {
  if (MODE !== 'server') return;
  const id = String(loginId || '').trim();
  try {
    await api('POST', '/api/login', { loginId: id, password });
  } catch (err) {
    if (err.status === 401 || err.status === 400) throw new Error('Incorrect name or password.');
    // 429 carries the server's wait time ("Try again in 12 minutes."); 403 = cross-origin refusal.
    if (err.status === 429) throw new Error(err.message || 'Too many attempts. Try again later.');
    throw new Error(err.message || 'Could not sign in. Try again.');
  }
  writePref(LOGIN_KEY, id);
  update({ notice: '' });
  await load({ checkSession: true });
  return snapshot.user; // the signed-in user (temporaryPassword → the sign-in form opens Settings)
}

export async function logout() {
  if (MODE !== 'server' || snapshot.authRequired === false) return; // nothing to sign out of
  try {
    await api('POST', '/api/logout');
  } catch {
    // Show the sign-in screen regardless; the cookie is cleared server-side when the request succeeds.
  }
  signedOut({ notice: '' });
}

// Settings → Account → Change password. Rejects with a message fit for the form.
export async function changePassword(currentPassword, newPassword) {
  if (MODE !== 'server') throw new Error('Passwords exist only on the server.');
  try {
    await api('POST', '/api/account/password', { currentPassword, newPassword });
  } catch (err) {
    if (err.status !== 401) throw new Error(err.message);
    // 401: a wrong current password, or the session itself is gone — ask which.
    let session;
    try {
      session = await adapter.session();
    } catch {
      session = null;
    }
    if (!session) {
      signedOut();
      throw new Error('Your session has expired. Sign in again.');
    }
    throw new Error('Current password is incorrect.');
  }
  if (snapshot.user?.temporaryPassword) update({ user: { ...snapshot.user, temporaryPassword: false } });
}

// People → Reset password (admins). The member's other sessions end on the server.
export async function resetPassword(memberId, newPassword) {
  if (MODE !== 'server') throw new Error('Passwords exist only on the server.');
  try {
    await api('POST', `/api/users/${encodeURIComponent(memberId)}/password`, { newPassword });
  } catch (err) {
    if (err.status === 401) signedOut();
    throw new Error(err.message);
  }
}
